import { Box, LinkButton, Typography } from "@bca/ui-library";
import { FunctionComponent, useEffect, useState } from "react";
import ExclamationCircleBoldSVG from "../Icons/ExclamationCircleBoldSVG";
import Colours from "../../types/colours";
import { SEARCH_URL, VENDOR_PORTAL_URL } from "../../helpers/pageUrls";
import { ParsedUrlQuery } from "querystring";
import { useRouter } from "next/router";
import { StoreType } from "../../redux/StoreType";
import { shallowEqual, useSelector } from "react-redux";
import { GET_VENDOR_IMPERSONATION_STATE } from "../../redux/slices/impersonationSlice";

enum VendorPortalPages {
  SOLD = "sold",
  PROVISIONALS = "provisionals",
  VENDOR = "vendor",
}

interface ImpersonationModalContentProps {
  isMobileDevice: boolean;
  vrm: string;
}

const ImpersonationModalContent: FunctionComponent<
  ImpersonationModalContentProps
> = ({ isMobileDevice, vrm }) => {
  const router = useRouter();

  const {
    impersonation: { isImpersonationSessionExpired },
  }: StoreType = useSelector(GET_VENDOR_IMPERSONATION_STATE, shallowEqual);
  const [isProvisionals, setIsProvisionals] = useState(false);
  const [isSold, setIsSold] = useState(false);
  const [isVendor, setIsVendor] = useState<boolean>(false);

  const [stripedVrm, setStripedVrm] = useState<string>("");
  const [filtersUrlQueryString, setFiltersUrlQueryString] =
    useState<string>("");

  const getBackToVendorPortalUrl = (page?: string) =>
    `${VENDOR_PORTAL_URL}${page ? `/${page}` : ""}${
      filtersUrlQueryString === "?" ? "?" : `${filtersUrlQueryString}&`
    }vrm=${stripedVrm}`;

  const getFiltersUrlQueryString = (
    originalQuery: ParsedUrlQuery,
    sourcePage: VendorPortalPages
  ): string => {
    const query = Object.assign({}, originalQuery);
    let path = "";
    delete query.vdr;
    delete query.requestOrigin;
    if (sourcePage === VendorPortalPages.SOLD) {
      delete query.source;
      delete query.inventoryID;
      delete query.filterDate;
    } else if (sourcePage === VendorPortalPages.PROVISIONALS) {
      delete query.source;
    }
    let counter = 0;
    for (const property in query) {
      counter++;
      if (counter === Object.keys(query).length)
        path += `${property}=${query[property]}`;
      else path += `${property}=${query[property]}&`;
    }
    if (path.includes("|IsProvisional:false"))
      return path.replace("|IsProvisional:false", "");
    return path.replace("IsProvisional:false", "");
  };

  function removeWhiteSpace(vrm: string) {
    return vrm.replace(/\s+/g, "");
  }

  const backToSearch = filtersUrlQueryString
    ? `${SEARCH_URL}${filtersUrlQueryString}&vrm=${stripedVrm}`
    : `${SEARCH_URL}?vrm=${stripedVrm}`;

  useEffect(() => {
    setIsVendor(window.location.search.includes("vdr=vp"));
    setIsProvisionals(window.location.search.includes("source=Provisionals"));
    setIsSold(window.location.search.includes("source=Sold"));
  }, []);

  useEffect(() => {
    setStripedVrm(removeWhiteSpace(vrm));
  }, [vrm]);

  useEffect(() => {
    if (isSold) {
      setFiltersUrlQueryString(
        `?${getFiltersUrlQueryString(router.query, VendorPortalPages.SOLD)}`
      );
    } else if (isProvisionals) {
      setFiltersUrlQueryString(
        `?${getFiltersUrlQueryString(
          router.query,
          VendorPortalPages.PROVISIONALS
        )}`
      );
    } else if (isVendor) {
      setFiltersUrlQueryString(
        `?${getFiltersUrlQueryString(router.query, VendorPortalPages.VENDOR)}`
      );
    } else {
      let searchParams = new URL(window.location.href).searchParams;
      searchParams.delete("searchVersion");
      setFiltersUrlQueryString(`?${searchParams.toString()}`);
    }
  }, [filtersUrlQueryString, isVendor, isProvisionals, isSold]);

  return (
    <>
      {isImpersonationSessionExpired && (
        <Box
          display="flex"
          flexDirection="column"
          pt={isMobileDevice ? "40px" : "34px"}
          pr={isMobileDevice ? 5 : 6}
          pb={isMobileDevice ? "23px" : 6}
          pl={isMobileDevice ? 5 : "34px"}
          data-testid="impersonation-modal-content"
        >
          <Box display="flex" flexDirection="column">
            <Typography variant={isMobileDevice ? "h4" : "h3"} my={0}>
              Your session has expired
            </Typography>
          </Box>

          <Box
            height={isMobileDevice ? "84px" : "66px"}
            mt="30px"
            display="flex"
            alignItems="center"
            backgroundColor={Colours.PINK_INFO_BACKGROUND}
            data-testid="impersonation-session-expiry-container"
          >
            <Box
              display="flex"
              height="100%"
              alignItems={isMobileDevice ? "flex-start" : "center"}
              ml={isMobileDevice ? "15px" : "25px"}
              mr={3}
            >
              <ExclamationCircleBoldSVG
                mt={isMobileDevice ? "20px" : 0}
                width="25px"
                height="24px"
                fill="primary-red"
              />
            </Box>
            <Box display="flex" alignItems="center" height="100%">
              <Typography
                variant="body-small"
                mb={0}
                mt={0}
                data-testid="expiry-message"
              >
                Your session {!isMobileDevice && `has `} expired. To begin a new
                session, please return to MyBCA Selling.
              </Typography>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            mt={isMobileDevice ? 4 : "50px"}
            justifyContent="flex-end"
          >
            <LinkButton
              text="Return"
              width={isMobileDevice ? "83px" : "160px"}
              height={isMobileDevice ? "42px" : "48px"}
              fontSize={1}
              ml={isMobileDevice ? 2 : 3}
              fontWeight={isMobileDevice ? "medium" : "bold"}
              shouldOpenInNewWindow={false}
              link={
                isProvisionals
                  ? getBackToVendorPortalUrl(VendorPortalPages.PROVISIONALS)
                  : isSold
                  ? getBackToVendorPortalUrl(VendorPortalPages.SOLD)
                  : isVendor
                  ? getBackToVendorPortalUrl()
                  : backToSearch
              }
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default ImpersonationModalContent;
